import EngineModule from '@/store/engine/modules/EngineModule';
import { Store } from '@/store/mainStore';
import { SavedRx, SavedRxDeleteInput, SavedRxInput, SavedRxName, SavedRxRenameAndSaveInput, SavedRxRenameInput, SavedrxService } from '../openapi/generated';
import { compareSavedRxes } from '@/utils';

class CachedSavedRx {
	name?: string;
	rx?: SavedRx;
}

export type SavedRxNames = Array<SavedRxName>;

export default class SavedRxModule extends EngineModule {
	private _rxList?: SavedRxNames;
	private cachedSavedRx: Array<CachedSavedRx> = [];

	constructor(s: Store) {
		super(s);
	}

	private async getRxNameList() {
		return SavedrxService.savedRxControllerGetSavedRxList(this.store.engine.store.getStore.companyID)
			.then((r) => r)
			.catch((e) => []);
	}

	private findSavedRx(name: string) {
		return this.cachedSavedRx.find((rx) => rx.name === name);
	}

	public async getRx(name: string, companyID?: string) {
		const cached = this.findSavedRx(name);
		if (!cached) {
			const savedRx = await SavedrxService.savedRxControllerGetSavedRx(name, companyID);
			if (savedRx) {
				this.cachedSavedRx.push({ name: savedRx.fileName, rx: savedRx });
				return savedRx;
			} else {
				throw new Error('No rx fetched for name: ' + name);
			}
		} else {
			return cached.rx;
		}
	}

	public async getRxList(cache = true) {
		if (!this._rxList || !cache) {
			let rxList = await this.getRxNameList();
			rxList.sort(compareSavedRxes);
			this._rxList = rxList;
		}

		return this._rxList;
	}

	public async upsertSavedRx(fileName: string, fileData: string) {
		this._rxList = undefined;
		this.cachedSavedRx = this.cachedSavedRx.filter((rx) => rx.name !== fileName);
		let input: SavedRxInput = {
			fileName: fileName,
			fileData: fileData,
			companyID: this.store.engine.store.getStore.companyID
		};
		return SavedrxService.savedRxControllerUpsertSavedRx(input);
	}

	public async isRxAvailable(fileName: string) {
		return (await this.getRxList(false)).filter((rx) => rx.fileName === fileName).length;
	}

	public async deleteRx(fileName: string) {
		this._rxList = undefined;
		this.cachedSavedRx = this.cachedSavedRx.filter((rx) => rx.name !== fileName);
		let input: SavedRxDeleteInput = {
			fileName: fileName,
			companyID: this.store.engine.store.getStore.companyID
		};
		return await SavedrxService.savedRxControllerDeleteSavedRx(input);
	}

	public async renameRx(fileName: string, newFileName: string) {
		this._rxList = undefined;
		this.cachedSavedRx = this.cachedSavedRx.filter((rx) => rx.name !== fileName);
		let input: SavedRxRenameInput = {
			fileName: fileName,
			newFileName: newFileName,
			companyID: this.store.engine.store.getStore.companyID
		};
		return await SavedrxService.savedRxControllerRenameSavedRx(input);
	}

	public async renameAndSaveRx(fileName: string, newFileName: string, fileData: string) {
		this._rxList = undefined;
		this.cachedSavedRx = this.cachedSavedRx.filter((rx) => rx.name !== fileName);
		let input: SavedRxRenameAndSaveInput = {
			fileName: fileName,
			newFileName: newFileName,
			fileData: fileData,
			companyID: this.store.engine.store.getStore.companyID
		};

		return await SavedrxService.savedRxControllerRenameAndSaveSavedRx(input);
	}
}
